// Privacy Policy + Terms of Service for the air surfaces. One file, two exports, both wrapped in the shared
// Layout so they inherit the navo24 chrome. Concise, plain-English, and vendor-neutral. Navo24 is the
// operating entity; AirCargoMCP / AirRatesMCP are its products. Prose is em-dash-free (brand voice gate).

import type { ReactNode } from 'react'
import { Layout } from '../components/Layout'
import { surface } from '../surface'

const UPDATED = '30 July 2026'
const BRAND = `${surface.wordA}${surface.wordB}MCP`

function LegalShell({ kind, children }: { kind: string; children: ReactNode }) {
  return (
    <Layout>
      <article
        style={{
          maxWidth: 720,
          margin: '0 auto',
          padding: '64px 24px 96px',
          fontFamily: "'Switzer', system-ui, sans-serif",
          color: 'rgb(var(--muted-rgb))',
          fontSize: 15,
          lineHeight: 1.75,
        }}
      >
        <p
          className="mono"
          style={{
            fontSize: 12,
            letterSpacing: '.14em',
            textTransform: 'uppercase',
            color: 'rgb(var(--signal-rgb))',
            margin: 0,
          }}
        >
          {kind}
        </p>
        <h1
          style={{
            fontFamily: "'Ranade', Georgia, serif",
            fontSize: 34,
            fontWeight: 600,
            letterSpacing: '-.02em',
            color: 'rgb(var(--ink-rgb))',
            margin: '10px 0 6px',
          }}
        >
          {kind === 'Privacy' ? 'Privacy Policy' : 'Terms of Service'}
        </h1>
        <p
          className="mono"
          style={{ fontSize: 12.5, color: 'rgb(var(--faint-rgb))', marginTop: 0 }}
        >
          {BRAND} · a Navo24 product · last updated {UPDATED}
        </p>
        <div style={{ marginTop: 28, display: 'flex', flexDirection: 'column', gap: 22 }}>
          {children}
        </div>
      </article>
    </Layout>
  )
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section>
      <h2
        style={{
          fontFamily: "'Ranade', Georgia, serif",
          fontSize: 19,
          fontWeight: 600,
          color: 'rgb(var(--ink-rgb))',
          margin: '0 0 8px',
        }}
      >
        {title}
      </h2>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>{children}</div>
    </section>
  )
}

const mail = (addr: string) => (
  <a href={`mailto:${addr}`} style={{ color: 'rgb(var(--signal-rgb))' }}>
    {addr}
  </a>
)

export function PrivacyPage() {
  return (
    <LegalShell kind="Privacy">
      <p>
        {BRAND} is operated by Navo24. This policy explains what we collect when you use the
        website, the API, or the MCP server, and how we handle it. We keep it short because we
        collect little.
      </p>
      <Section title="What we collect">
        <p>
          <b>Lookups.</b> When you resolve an air waybill, an AWB prefix, a flight, or a rate lane,
          we log the reference, the timestamp, and coarse request metadata (IP, user agent) so we
          can rate-limit, prevent abuse, meter API usage against your plan, and cache results.
          Shipment references are operational data, not marketing data.
        </p>
        <p>
          <b>Account.</b> If you hold an API key or a workspace, we store the account email and the
          plan entitlements tied to it. Authentication is handled by our identity provider; we never
          see your password.
        </p>
        <p>
          <b>Product analytics.</b> Aggregate, privacy-respecting usage counts (page views, endpoint
          calls) so we can improve the product. No cross-site tracking, no ad networks.
        </p>
      </Section>
      <Section title="What we do not do">
        <p>
          We do not sell your data. We do not share shipment references with advertisers. We do not
          build profiles of you across other sites.
        </p>
      </Section>
      <Section title="Sub-processors">
        <p>
          We rely on infrastructure and data providers (hosting, our identity provider, and the
          carrier and global-distribution-system sources that answer a lookup) strictly to deliver
          the service. They process data on our behalf under their own terms.
        </p>
      </Section>
      <Section title="Retention & your rights">
        <p>
          Operational logs are retained only as long as needed for security, billing, and caching,
          then rotated. You can request access to, correction of, or deletion of your account data
          at any time. Write to {mail('privacy@navo24.com')} and we will action it.
        </p>
      </Section>
      <Section title="Contact">
        <p>Questions about privacy: {mail('privacy@navo24.com')}.</p>
      </Section>
    </LegalShell>
  )
}

export function TosPage() {
  return (
    <LegalShell kind="Terms">
      <p>
        These terms govern your use of {BRAND}, operated by Navo24. By using the website, the API,
        or the MCP server, you agree to them.
      </p>
      <Section title="The service">
        <p>
          {BRAND} returns air-cargo tracking and rate information aggregated from carriers, shared
          tracking platforms, and global-distribution-system sources. Data is provided on a
          best-effort basis and may be delayed or incomplete. It is informational and is not a
          contract of carriage, a booking, or a guarantee of delivery.
        </p>
      </Section>
      <Section title="Acceptable use">
        <p>
          Use the API within your plan limits and the published rate limits. Do not scrape around
          the API, resell raw responses as a competing data feed, probe or attack the service, or
          use it to violate any carrier or third-party terms. We may suspend keys that abuse the
          service.
        </p>
      </Section>
      <Section title="Plans, billing & keys">
        <p>
          Paid plans and allowances are set out in your order or invoice. A service is enabled once
          its invoice is paid and stays within the allowance for the billing period. API keys are
          yours to keep secret; you are responsible for calls made with them.
        </p>
      </Section>
      <Section title="No warranty; liability">
        <p>
          The service is provided as is, without warranties of accuracy, availability, or fitness
          for a particular purpose. To the extent permitted by law, Navo24 is not liable for
          indirect or consequential losses, or for decisions made on the basis of the data. Nothing
          here limits liability that cannot be limited by law.
        </p>
      </Section>
      <Section title="Changes & contact">
        <p>
          We may update these terms; the effective date above reflects the latest version.
          Questions: {mail('legal@navo24.com')}.
        </p>
      </Section>
    </LegalShell>
  )
}
